/**
 * PerspectiveAssignedContent - Renders content for PerspectiveAssigned/PerspectiveUnassigned timeline events
 * Displays a horizontal relationship card: Subject sphere -> Perspective label -> Target sphere
 * with actor + timestamp footer
 */

import React, { useState, useEffect } from 'react';
import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'localization';
import { TimelineEvent } from 'types/timeline';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { ApiClient } from '@center-technologies/shared-api-clients';
import SphereAvatar from '../../sphere-avatar/sphere-avatar';
import type { GlyphColor } from '../../../../types/glyph';
import { formatRelativeTime } from '../SphereTimelineEventMolecule';
import './PerspectiveAssignedContent.scss';

interface PerspectiveAssignedContentProps {
  event: TimelineEvent;
}

interface ResolvedSphere {
  name: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
  sphereId: string;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const formatPerspectiveName = (systemName: string | undefined): string => {
  if (!systemName) return '';
  return systemName
    .replace(/^center_/, '')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
};

const resolveSphere = async (
  graphClient: ApiClient,
  id: string,
  preferredName?: string,
): Promise<ResolvedSphere> => {
  if (!UUID_REGEX.test(id)) {
    return { name: preferredName || id, sphereId: id };
  }
  try {
    const cachedData = await globalSphereCache.getSphereData(id, graphClient);
    const sphere = cachedData.info?.sphere;
    if (sphere) {
      return {
        // Payload name wins for display; getSphereData provides avatar data + name fallback
        name: preferredName || sphere.properties?.name || id,
        glyph: sphere.properties?.glyph,
        glyphColor: sphere.properties?.glyphColor,
        imageId: sphere.properties?.imageId,
        sphereId: sphere.id || id,
      };
    }
  } catch {
    // Fallback to payload name or raw ID on resolution failure
  }
  return { name: preferredName || id, sphereId: id };
};

const SphereSkeleton: React.FC<{ width?: string }> = ({ width = '80px' }) => (
  <div className="perspective-assignment__sphere">
    <Skeleton shape="circle" size="24px" />
    <Skeleton width={width} height="12px" borderRadius="4px" />
  </div>
);

const PerspectiveAssignedContent: React.FC<PerspectiveAssignedContentProps> = ({ event }) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { payload, actor, timestamp, eventType, sphereId } = event;

  const subjectId = payload?.subjectId as string | undefined;
  // Phase 3: names come from payload for immediate display — getSphereData only enriches avatars
  const subjectNameFromPayload = payload?.subjectName as string | undefined;
  const targetSphereNameFromPayload = payload?.targetSphereName as string | undefined;
  const perspectiveSystemName = payload?.perspectiveSystemName;
  // Use payload.targetSphereId when available (bidirectional events), fall back to event.sphereId
  const targetSphereId = (payload?.targetSphereId as string | undefined) || sphereId;
  const isUnassigned = eventType === 'perspective_unassigned';

  // Initialize with payload names for immediate display; null when no payload name (skeleton shown)
  const [subject, setSubject] = useState<ResolvedSphere | null>(
    subjectNameFromPayload ? { name: subjectNameFromPayload, sphereId: subjectId || '' } : null,
  );
  const [performer, setPerformer] = useState<ResolvedSphere | null>(null);
  const [targetSphere, setTargetSphere] = useState<ResolvedSphere | null>(
    targetSphereNameFromPayload ? { name: targetSphereNameFromPayload, sphereId: targetSphereId } : null,
  );

  useEffect(() => {
    if (!apiClients?.graphClient) return;

    let cancelled = false;

    const fetchInfo = async () => {
      const [resolvedSubject, resolvedPerformer, resolvedTargetSphere] = await Promise.all([
        subjectId
          ? resolveSphere(apiClients.graphClient, subjectId, subjectNameFromPayload)
          : Promise.resolve({ name: subjectNameFromPayload || t('common.unknown'), sphereId: '' }),
        actor
          ? resolveSphere(apiClients.graphClient, actor)
          : Promise.resolve({ name: t('common.unknown'), sphereId: '' }),
        targetSphereId
          ? resolveSphere(apiClients.graphClient, targetSphereId, targetSphereNameFromPayload)
          : Promise.resolve({ name: targetSphereNameFromPayload || t('common.unknown'), sphereId: '' }),
      ]);

      if (!cancelled) {
        setSubject(resolvedSubject);
        setPerformer(resolvedPerformer);
        setTargetSphere(resolvedTargetSphere);
      }
    };

    fetchInfo();

    return () => {
      cancelled = true;
    };
  }, [subjectId, subjectNameFromPayload, actor, targetSphereId, targetSphereNameFromPayload, apiClients, t]);

  const rootClassName = [
    'perspective-assignment',
    isUnassigned ? 'perspective-assignment--unassigned' : '',
  ].filter(Boolean).join(' ');

  const perspectiveLabel = formatPerspectiveName(perspectiveSystemName);

  return (
    <div className={rootClassName}>
      <div className="perspective-assignment__relationship">
        {/* Subject sphere (left) */}
        {subject ? (
          <div className="perspective-assignment__sphere">
            <SphereAvatar
              glyph={subject.glyph}
              glyphColor={subject.glyphColor}
              sphereId={subject.sphereId}
              sphereName={subject.name}
              imageId={subject.imageId}
              size="small"
            />
            <span className="perspective-assignment__name">{subject.name}</span>
          </div>
        ) : (
          <SphereSkeleton width="90px" />
        )}

        {/* Connector (center) */}
        <div className="perspective-assignment__connector">
          {perspectiveLabel && (
            <span className="perspective-assignment__perspective-label">
              {perspectiveLabel}
            </span>
          )}
          <div className="perspective-assignment__arrow" />
        </div>

        {/* Target sphere (right) */}
        {targetSphere ? (
          <div className="perspective-assignment__sphere">
            <SphereAvatar
              glyph={targetSphere.glyph}
              glyphColor={targetSphere.glyphColor}
              sphereId={targetSphere.sphereId}
              sphereName={targetSphere.name}
              imageId={targetSphere.imageId}
              size="small"
            />
            <span className="perspective-assignment__name">{targetSphere.name}</span>
          </div>
        ) : (
          <SphereSkeleton width="70px" />
        )}
      </div>

      {/* Footer: actor + timestamp */}
      <div className="perspective-assignment__footer">
        {performer ? (
          <>
            <SphereAvatar
              glyph={performer.glyph}
              glyphColor={performer.glyphColor}
              sphereId={performer.sphereId}
              sphereName={performer.name}
              imageId={performer.imageId}
              size="xsmall"
              className="perspective-assignment__actor-avatar"
            />
            <span className="perspective-assignment__actor">{performer.name}</span>
          </>
        ) : (
          <Skeleton width="60px" height="10px" borderRadius="4px" />
        )}
        <span className="perspective-assignment__separator">&middot;</span>
        <span className="perspective-assignment__time">
          {formatRelativeTime(timestamp, t)}
        </span>
      </div>
    </div>
  );
};

export default PerspectiveAssignedContent;
